import { Inject, Injectable } from '@angular/core';
import { EncryptionService } from '@evorait/pace-encryption';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import Dexie from 'dexie';
import { catchError, combineLatest, from, Observable, of } from 'rxjs';
import { PCHAlert } from '../pch-alerts/pch-alert.interface';
import { mergeMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PCHIndexedDBService extends Dexie {
    public static readonly DB_NAME = 'AlertsDatabase';
    public static readonly DB_TABLE = 'alertsEntries';
    public static readonly DB_KEY_PATH = 'id';
    private static readonly VERSION_ONE = 1;

    private alertsEntries: Dexie.Table<PCHAlert, string>;
    public constructor(
        private readonly encryption: EncryptionService,
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
    ) {
        super(PCHIndexedDBService.DB_NAME);

        this.version(PCHIndexedDBService.VERSION_ONE).stores({
            [PCHIndexedDBService.DB_TABLE]: PCHIndexedDBService.DB_KEY_PATH,
        });
        this.alertsEntries.mapToClass(PCHAlert);
    }
    public loadAllPCHAlertsFromIndexedDB(): Observable<PCHAlert[]> {
        return from(this.alertsEntries.orderBy(PCHIndexedDBService.DB_KEY_PATH).toArray()).pipe(
            mergeMap((entries) => {
                if (entries.length < 1) {
                    return of([]);
                }
                return combineLatest(
                    entries.map(async (entry) => ({
                        ...entry,
                        message: await this.encryption.decryptAsync(entry.message),
                    })),
                );
            }),
            catchError((error) => {
                this.log.error(`PCHIndexedDBService: loadAllPCHAlertsFromIndexedDB: Error: ${error}`);
                return of([]);
            }),
        );
    }

    /**
     * Write an alert entry to the database
     *
     * @param alert Alert object to save
     * @returns Observable that completes when the write operation is done
     */
    public savePCHAlertToIndexedDB(alert: PCHAlert): Observable<unknown> {
        return from(this.encryption.encryptAsync(alert.message)).pipe(
            mergeMap((message) =>
                this.alertsEntries.put({
                    time: alert.time,
                    title: alert.title,
                    message,
                    id: alert.id,
                }),
            ),
            catchError((err) => {
                throw Error(`PCHIndexedDBService: savePCHAlertToIndexedDB: ${err}`);
            }),
        );
    }

    /**
     * Remove alert entry from the database.
     *
     * @param id id to delete
     * @returns Observable with the result of the delete operation
     */
    public deleteAlerts(id: string): Observable<number> {
        return from(this.alertsEntries.where({ id }).delete()).pipe(
            catchError((error) => {
                throw Error(`PCHIndexedDBService: deleteAlerts: Error on deleting entry: ${error}`);
            }),
        );
    }
}
